import type { IVector3 } from '@seddi/math-v2';
import {
  EventDispatcher,
  type EventListener,
  type PerspectiveCamera,
  Quaternion,
  type QuaternionLike,
  Vector3,
  type Vector3Like,
} from '@seddi/three';

import { OrbitControls } from '../../viewer/orbitControls';

export type CameraChangeData = {
  position: Readonly<Vector3>;
  quaternion: Readonly<Quaternion>;
};

export type CameraChangeListener = EventListener<CameraChangeData, 'cameraChange', unknown>;

type ConstructorArgs = {
  camera: PerspectiveCamera;
  canvas: HTMLCanvasElement;
};

type EventMap = { cameraChange: CameraChangeData };

export class AppViewerTool extends EventDispatcher<EventMap> {
  readonly camera: PerspectiveCamera;
  readonly canvas: HTMLCanvasElement;

  private readonly orbitControls: OrbitControls;

  private readonly cameraPositionPrev = new Vector3();
  private readonly cameraRotationPrev = new Quaternion();

  dispose: () => void;

  constructor({ camera, canvas }: ConstructorArgs) {
    super();

    this.camera = camera;
    this.canvas = canvas;

    // init orbit controls
    this.orbitControls = new OrbitControls(camera, canvas);
    this.orbitControls.dampingFactor = 0.1;
    this.orbitControls.enableDamping = true;

    // init default camera
    this.lookAt({ x: 0.0, y: 1.2, z: 3.0 }, { x: 0.0, y: 1.2, z: 0.0 });

    this.dispose = function () {
      this.orbitControls.dispose();
    };
  }

  lookAt(origin: IVector3, target: IVector3) {
    this.camera.position.copy(origin);
    this.camera.lookAt(target.x, target.y, target.z);
    this.orbitControls.target.copy(target);
  }

  onAnimationTick(): boolean {
    this.orbitControls.update();

    const isCameraPositionDirty = !approxEqualsVector3(
      this.cameraPositionPrev,
      this.camera.position,
      1e-4,
    );
    const isCameraRotationDirty = !approxEqualsQuaternion(
      this.cameraRotationPrev,
      this.camera.quaternion,
      1e-4,
    );
    const isCameraTransformDirty = isCameraPositionDirty || isCameraRotationDirty;

    if (isCameraTransformDirty) {
      this._emitCameraChange();
    }

    this.cameraPositionPrev.copy(this.camera.position);
    this.cameraRotationPrev.copy(this.camera.quaternion);

    return isCameraTransformDirty;
  }

  onUpdate() {
    // Do nothing.
  }

  _emitCameraChange() {
    const { camera } = this;
    const { position, quaternion } = camera;
    this.dispatchEvent({ position, quaternion, type: 'cameraChange' });
  }
}

const approxEqualsVector3 = (a: Vector3Like, b: Vector3Like, epsilon = Number.EPSILON) => {
  return (
    Math.abs(a.x - b.x) < epsilon && Math.abs(a.y - b.y) < epsilon && Math.abs(a.z - b.z) < epsilon
  );
};

const approxEqualsQuaternion = (a: QuaternionLike, b: QuaternionLike, epsilon = Number.EPSILON) => {
  return (
    Math.abs(a.w - b.w) < epsilon &&
    Math.abs(a.x - b.x) < epsilon &&
    Math.abs(a.y - b.y) < epsilon &&
    Math.abs(a.z - b.z) < epsilon
  );
};
